import React, { ReactNode } from 'react';
import { DotPosition } from 'antd/es/carousel';
import {
  StyledAppendDotsContainer,
  StyledAppendDotsInner,
  StyledCarousel,
  StyledCustomPaging,
} from './style';
import { ProductData, ProductImageProps } from 'types/ProductData';
import { FarmData } from 'types/FarmData';
import { Card } from '../Card';
import { FarmCard } from '../Card/FarmCard';
import { CategoryCard } from 'ui/Card/CategoryCard';
import { CategoryData } from 'types/CategoryData';
import { FallbackImage } from '../UploadImage/fallBack/FallBackImage';

export interface ResponsiveObject {
  breakpoint: number;
  settings: 'unslick' | Settings;
}

export interface Settings {
  slidesToShow?: number | undefined;
}

export interface CarouselProps {
  arrows?: boolean;
  autoplay?: boolean;
  autoplaySpeed?: number;
  adaptiveHeight?: boolean;
  dotPosition?: DotPosition;
  dots?: boolean | { className?: string };
  draggable?: boolean;
  fade?: boolean;
  infinite?: boolean;
  speed?: number;
  easing?: string;
  effect?: 'scrollx' | 'fade';
  afterChange?: (current: number) => void;
  beforeChange?: (current: number, next: number) => void;
  waitForAnimate?: boolean;
  productImages?: ProductImageProps[];
  slidesToShow?: number;
  slidesPerRow?: number;
  data?: ProductData[] | FarmData[];
  dataType?: 'products' | 'farms';
  products?: ProductData[];
  background?: boolean;
  dotsClass?: string;
  popularCategory?: CategoryData[];
  responsive?: ResponsiveObject[];
  customDots?: boolean;
  customPrevArrow?: React.JSX.Element;
  customNextArrow?: React.JSX.Element;
  children?: (slotProps: {
    category: CategoryData;
    index: number;
  }) => React.ReactNode;
}

export const Carousel = ({
  arrows,
  autoplay,
  autoplaySpeed,
  adaptiveHeight,
  dotPosition,
  dots,
  draggable,
  fade,
  infinite = false,
  speed,
  easing,
  effect,
  afterChange,
  beforeChange,
  waitForAnimate,
  slidesPerRow,
  slidesToShow,
  data,
  dataType = 'products',
  popularCategory,
  responsive,
  customDots = true,
  productImages,
  customPrevArrow,
  customNextArrow,
  children,
}: CarouselProps) => {
  const items = data ?? [];
  const type = dataType ?? 'products';
  const itemCount = items.length;
  const showDots = customDots && (dots || itemCount > 1);

  return (
    <StyledCarousel
      responsive={responsive}
      arrows={arrows}
      autoplay={autoplay}
      autoplaySpeed={autoplaySpeed}
      adaptiveHeight={adaptiveHeight}
      dotPosition={dotPosition}
      dots={showDots}
      draggable={draggable}
      fade={fade}
      infinite={infinite}
      speed={speed}
      easing={easing}
      effect={effect}
      afterChange={afterChange}
      beforeChange={beforeChange}
      waitForAnimate={waitForAnimate}
      slidesPerRow={slidesPerRow}
      slidesToShow={slidesToShow}
      customPaging={(i: number) =>
        productImages && productImages[i] ? (
          <StyledCustomPaging key={i} className="custom-dot image-dots">
            <FallbackImage
              src={productImages[i].url_original}
              type={'FarmGalery'}
              alt={`thumbnail-${i}`}
              className={'custom-dot'}
              preview={false}
            />
          </StyledCustomPaging>
        ) : (
          <StyledCustomPaging key={i} className="custom-dot" />
        )
      }
      appendDots={
        !!productImages
          ? (dots: ReactNode) => (
              <StyledAppendDotsContainer>
                <StyledAppendDotsInner>{dots}</StyledAppendDotsInner>
              </StyledAppendDotsContainer>
            )
          : undefined
      }
      nextArrow={customNextArrow}
      prevArrow={customPrevArrow}
      className="slider"
    >
      {popularCategory &&
        popularCategory.length > 0 &&
        popularCategory.map((category, index) => (
          <div key={`product-category-${index}`}>
            {children ? (
              children({ category, index })
            ) : (
              <CategoryCard
                category={category}
                bordered={true}
                hoverable={true}
              />
            )}
          </div>
        ))}
      {items.length > 0 &&
        type === 'products' &&
        (items as ProductData[]).map((product, index) => (
          <Card
            className="carousel-card-product"
            product={product}
            imageUrl={product.image_small}
            hoverable
            key={`product-${index}`}
          />
        ))}
      {items.length > 0 &&
        type === 'farms' &&
        (items as FarmData[]).map((farm, index) => (
          <FarmCard
            className="carousel-card-farm"
            farm={farm}
            isFarmMap
            hoverable
            key={`farm-${farm?.id ?? index}`}
          />
        ))}

      {productImages &&
        productImages.length > 0 &&
        productImages.map((image, index) => (
          <div key={index} className={'gallery-img-container'}>
            <FallbackImage
              src={image.url_original}
              type={'FarmGalery'}
              preview={false}
            />
          </div>
        ))}
    </StyledCarousel>
  );
};
